import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Toaster } from '../../components/ui/sonner';
import { 
  FileText, 
  Inbox, 
  Image, 
  LogOut,
  Home,
  Menu,
  Video,
  Users,
  BarChart3,
  Mail,
  MessageSquare,
  Settings,
  History
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../../components/ui/sheet';

const getNavItems = (isAdmin) => {
  const items = [
    { href: '/staff/articles', label: 'Articles', icon: FileText },
    { href: '/staff/trending-videos', label: 'The Trade Shorts', icon: Video },
    { href: '/staff/submissions', label: 'Submissions', icon: Inbox },
    { href: '/staff/media', label: 'Media', icon: Image },
    { href: '/staff/comments', label: 'Comments', icon: MessageSquare },
    { href: '/staff/newsletter', label: 'Newsletter', icon: Mail },
    { href: '/staff/analytics', label: 'Analytics', icon: BarChart3 },
  ];
  
  // Only show admin-only items
  if (isAdmin) {
    items.push({ href: '/staff/users', label: 'Users', icon: Users });
    items.push({ href: '/staff/settings', label: 'Settings', icon: Settings });
    items.push({ href: '/staff/audit-logs', label: 'Audit Logs', icon: History });
  }
  
  return items;
};

export const AdminLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);
    
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  const navItems = getNavItems(isAdmin);

  const handleLogout = () => {
    logout();
    navigate('/staff/login');
  };

  const NavLinks = ({ onClick }) => (
    <>
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.href;
        return (
          <Link
            key={item.href}
            to={item.href}
            onClick={onClick}
            className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
              isActive 
                ? 'bg-secondary text-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            }`}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <Icon className="h-5 w-5" />
            {item.label}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="staff-mobile-menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64">
                <div className="py-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_contentcms-2/artifacts/ti47qc52_image1.webp" 
                    alt="The Trade" 
                    className="h-10 w-auto mb-6"
                  />
                  <nav className="space-y-1">
                    <NavLinks />
                  </nav>
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/staff/articles" data-testid="staff-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_contentcms-2/artifacts/ti47qc52_image1.webp" 
                alt="The Trade" 
                className="h-8 w-auto"
              />
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" data-testid="view-site-link">
              <Button variant="outline" size="sm">
                <Home className="h-4 w-4 mr-2" />
                View Site
              </Button>
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {user?.name}
              </span>
              <span className="text-xs px-2 py-0.5 bg-secondary rounded-full capitalize">
                {user?.role}
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              data-testid="staff-logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:flex md:flex-col w-64 border-r border-border">
          <nav className="flex-1 p-4 space-y-1">
            <NavLinks />
          </nav>
          
          {/* Sidebar Footer */}
          <div className="p-4 border-t border-border">
            <img 
              src="https://customer-assets.emergentagent.com/job_contentcms-2/artifacts/fo9a7wfg_Dream%2BBigger.%2BBuild%2BSmarter.%2BOwn%2BMore..webp" 
              alt="The Trade - Dream Bigger. Build Smarter. Own More." 
              className="h-12 w-auto opacity-60"
            />
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Staff Footer */}
      <footer className="bg-foreground text-background border-t border-border" data-testid="staff-footer">
        <div className="px-4 md:px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_contentcms-2/artifacts/fo9a7wfg_Dream%2BBigger.%2BBuild%2BSmarter.%2BOwn%2BMore..webp" 
                alt="The Trade" 
                className="h-10 w-auto"
              />
              <span className="text-sm text-background/70">Staff Portal</span>
            </div>
            <p className="text-sm text-background/50">
              © {new Date().getFullYear()} The Trade Media. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <Toaster position="bottom-right" />
    </div>
  );
};

export default AdminLayout;
